// src/hooks/useSalesData.js - REFRESH FIX
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import salesService from '../services/salesService';

export const useSalesData = (initialFilters = {}) => {
  const defaultFilters = {
    date_range: 'last_4_weeks',
    closer: 'all',
    offer_type: 'all',
    start_date: '',
    end_date: '',
    specific_date: '',
    date_filter_type: 'preset', // 'preset', 'range', 'specific'
    ...initialFilters
  };

  const [bulkData, setBulkData] = useState(null);
  const [rawData, setRawData] = useState([]);
  const [filteredData, setFilteredData] = useState({
    kpis: null,
    teamData: [],
    callStatus: null,
    offers: null
  });
  const [filterOptions, setFilterOptions] = useState({
    closers: ['all'],
    offer_types: ['all']
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(defaultFilters);
  const [lastUpdated, setLastUpdated] = useState(null);

  // ADD THIS: Refresh trigger state
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const abortControllerRef = useRef(null);

  useEffect(() => {
    // Cleanup function to cancel previous requests
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Convert date range presets to actual dates
  const getDateRangeFromPreset = useCallback((preset) => {
    const today = new Date();
    const formatDate = (date) => date.toISOString().split('T')[0];
    
    switch (preset) {
      case 'today':
        return { start_date: formatDate(today), end_date: formatDate(today) };
      case 'last_7_days':
        const week = new Date(today);
        week.setDate(today.getDate() - 7);
        return { start_date: formatDate(week), end_date: formatDate(today) };
      case 'last_4_weeks':
        const fourWeeks = new Date(today);
        fourWeeks.setDate(today.getDate() - 28);
        return { start_date: formatDate(fourWeeks), end_date: formatDate(today) };
      case 'last_30_days':
        const month = new Date(today);
        month.setDate(today.getDate() - 30);
        return { start_date: formatDate(month), end_date: formatDate(today) };
      case 'last_90_days':
        const quarter = new Date(today);
        quarter.setDate(today.getDate() - 90);
        return { start_date: formatDate(quarter), end_date: formatDate(today) };
      case 'this_month':
        const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        return { start_date: formatDate(thisMonthStart), end_date: formatDate(today) };
      case 'last_month':
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        return { start_date: formatDate(lastMonthStart), end_date: formatDate(lastMonthEnd) };
      default:
        return { start_date: null, end_date: null };
    }
  }, []);

  // Build API filters from current filter state
  const buildApiFilters = useCallback((currentFilters) => {
    const apiFilters = {};
    
    // Handle different date filter types
    if (currentFilters.date_filter_type === 'range' && currentFilters.start_date && currentFilters.end_date) {
      apiFilters.start_date = currentFilters.start_date;
      apiFilters.end_date = currentFilters.end_date;
    } else if (currentFilters.date_filter_type === 'specific' && currentFilters.specific_date) {
      apiFilters.start_date = currentFilters.specific_date;
      apiFilters.end_date = currentFilters.specific_date;
    } else if (currentFilters.date_range && currentFilters.date_range !== 'custom') {
      const dateRange = getDateRangeFromPreset(currentFilters.date_range);
      if (dateRange.start_date) apiFilters.start_date = dateRange.start_date;
      if (dateRange.end_date) apiFilters.end_date = dateRange.end_date;
    }
    
    // Include metadata to get filter options
    apiFilters.include_metadata = true;
    
    return apiFilters;
  }, [getDateRangeFromPreset]);

  // Apply client-side filters to raw data
  const applyClientSideFilters = useCallback((rawDataArray, currentFilters) => {
    
    if (!rawDataArray || !rawDataArray.length) {
      const emptyResult = {
        kpis: salesService._getEmptyKPIs(),
        teamData: [],
        callStatus: { showed: 0, no_show: 0, canceled: 0, confirmed: 0, total: 0, closer: currentFilters.closer },
        offers: { total_sent: 0, high_probability: 0, medium_probability: 0, low_probability: 0, by_type: {} }
      };
      return emptyResult;
    }
    
    try {
      const kpis = salesService.calculateKPIs(rawDataArray, currentFilters);  
      const teamData = salesService.calculateTeamPerformance(rawDataArray, currentFilters.closer);
      const callStatus = salesService.calculateCallStatus(rawDataArray, currentFilters.closer);
      const offers = salesService.calculateOffers(rawDataArray, currentFilters.closer, currentFilters.offer_type);
      const result = { kpis, teamData, callStatus, offers };

      return result;
    } catch (error) {
      console.error('❌ FILTER: Error in applyClientSideFilters:', error);
      throw error;
    }
  }, []);

  // Update filters and apply client-side filtering
  const updateFilters = useCallback((newFilters) => {
    
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    
    // If we have raw data, immediately apply client-side filters
    if (rawData.length > 0) {
      const filtered = applyClientSideFilters(rawData, updatedFilters);
      setFilteredData(filtered);
    }
    
    // Check if we need to refetch data (only for date changes)
    const needsRefetch = (
      newFilters.date_range !== filters.date_range ||
      newFilters.start_date !== filters.start_date ||
      newFilters.end_date !== filters.end_date ||
      newFilters.specific_date !== filters.specific_date ||
      newFilters.date_filter_type !== filters.date_filter_type
    );
  }, [filters, rawData, applyClientSideFilters]);

  // Convenience filter update methods
  const setCloser = useCallback((closer) => {
    updateFilters({ closer });
  }, [updateFilters]);

  const setOfferType = useCallback((offerType) => {
    updateFilters({ offer_type: offerType });
  }, [updateFilters]);

  const setDateRange = useCallback((dateRange) => {
    updateFilters({ 
      date_range: dateRange, 
      date_filter_type: 'preset',
      start_date: '',
      end_date: '',
      specific_date: ''
    });
  }, [updateFilters]);

  const setCustomDateRange = useCallback((startDate, endDate) => {
    updateFilters({
      date_filter_type: 'range',
      start_date: startDate,
      end_date: endDate,
      date_range: 'custom',
      specific_date: ''
    });
  }, [updateFilters]);

  const setSpecificDate = useCallback((specificDate) => {
    updateFilters({
      date_filter_type: 'specific',
      specific_date: specificDate,
      date_range: 'custom',
      start_date: '',
      end_date: ''
    });
  }, [updateFilters]);

  // Force refresh (clears cache and refetches)
   const refresh = useCallback(() => {
    
    // Clear the service cache
    salesService.clearCache();
    
    // Cancel current request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Set refreshing state
    setRefreshing(true);
    
    // Trigger a new data fetch by incrementing the refresh trigger
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Get cache information
  const getCacheInfo = useCallback(() => {
    return salesService.getCacheInfo();
  }, []);

  // Main data loading useEffect with AbortController
  useEffect(() => {
    const loadData = async () => {
      
      // Cancel any previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Create new AbortController for this request
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;
      
      setLoading(true);
      setError(null);
      
      try {
        // Check if request was cancelled
        if (signal.aborted) {
          console.log('⚠️ HOOK: Request aborted before API call');
          return;
        }
        
        const apiFilters = buildApiFilters(filters);
        const bulk = await salesService.getBulkSalesData(apiFilters);
        
        // Check if request was cancelled after API call
        if (signal.aborted) {
          console.log('⚠️ HOOK: Request aborted after API call, not updating state');
          return;
        }
        
        setBulkData(bulk);
        const rawDataArray = bulk.raw_data || [];
        setRawData(rawDataArray);
        
        if (bulk.filter_metadata) {
          setFilterOptions({
            closers: ['all', ...(bulk.filter_metadata.available_closers || [])],
            offer_types: ['all', ...(bulk.filter_metadata.available_offer_types || [])],
            statuses: ['all', ...(bulk.filter_metadata.available_statuses || [])],
            pipeline_stages: ['all', ...(bulk.filter_metadata.available_pipeline_stages || [])]
          });
        }
        
        // Check again before processing
        if (signal.aborted) {
          console.log('⚠️ HOOK: Request aborted before filtering');
          return;
        }
        
        const filtered = applyClientSideFilters(rawDataArray, filters);
        setFilteredData(filtered);
        const lastUpdatedDate = await salesService.getDataFreshness()
        // Extract the actual timestamp from the nested structure
        const actualTimestamp = lastUpdatedDate?.data_last_updated;

        setLastUpdated(actualTimestamp);
        
      } catch (err) {
        if (err.name === 'AbortError') {
          console.log('⚠️ HOOK: Request was aborted');
          return;
        }
        
        console.error('❌ HOOK: Error during data fetching:', err);
        setError('Failed to fetch sales data. Please try again later.');
      } finally {
        // Always set loading to false unless request was aborted
        if (!signal.aborted) {
          setLoading(false);
          setRefreshing(false);
        } else {
          console.log('⚠️ HOOK: Request aborted, not changing loading state');
        }
      }
    };

    loadData();
  }, [
    // Original dependencies
    filters.date_range, 
    filters.start_date, 
    filters.end_date, 
    filters.specific_date, 
    filters.date_filter_type,
    // ADD THIS: Include refreshTrigger so refresh button triggers new fetch
    refreshTrigger,
    buildApiFilters,
    applyClientSideFilters,
    filters
  ]);

  // Refresh effect
  useEffect(() => {
    if (refreshing) {
      // Reset refreshing state after starting the load
      setRefreshing(false);
    }
  }, [refreshing]);

  return {
    // Filtered data (ready to use)
    kpis: filteredData.kpis,
    teamData: filteredData.teamData,
    callStatus: filteredData.callStatus,
    offers: filteredData.offers,
    
    // Raw data (for advanced use cases)
    bulkData,
    rawData,
    
    // Filter options (for dropdowns)
    filterOptions,
    
    // State
    loading,
    refreshing,
    error,
    filters,
    lastUpdated,
    
    // Filter methods
    updateFilters,
    setCloser,
    setOfferType,
    setDateRange,
    setCustomDateRange,
    setSpecificDate,
    
    // Actions
    refresh, // This now properly triggers a fresh data fetch
    getCacheInfo,
    
    // Computed values
    isEmpty: !filteredData.kpis || filteredData.teamData.length === 0,
    hasData: !!filteredData.kpis,
    dataInfo: {
      totalRecords: rawData.length,
      filteredRecords: rawData.length,
      cacheValid: salesService.isCacheValid()
    }
  };
};

// =============================================================================
// SPECIALIZED HOOKS
// =============================================================================

/**
 * Hook specifically for performance filters matching your existing component
 */
export const usePerformanceFilters = (initialFilters = {}) => {
  const defaultFilters = {
    date_range: 'last_4_weeks',
    closer: 'all',
    offer_type: 'all',
    start_date: '',
    end_date: '',
    specific_date: '',
    date_filter_type: 'preset'
  };

  const salesData = useSalesData({ ...defaultFilters, ...initialFilters });
  
  // Additional performance-specific methods
  const setPerformanceFilters = useCallback((newFilters) => {
    salesData.updateFilters(newFilters);
  }, [salesData]);

  return {
    ...salesData,
    
    // Performance-specific methods
    setPerformanceFilters,
    
    // Performance-specific computed values
    performanceData: {
      teamData: salesData.teamData,
      callStatus: salesData.callStatus,
      offers: salesData.offers
    },
    
    // Loading state specifically for performance section
    performanceLoading: salesData.loading || salesData.refreshing,
    
    // Data info for debugging
    dataInfo: {
      teamCount: salesData.teamData.length,
      callsCount: salesData.callStatus?.total || 0,
      offersCount: salesData.offers?.total_sent || 0,
      rawDataCount: salesData.rawData.length
    }
  };
};

/**
 * Lightweight hook for KPI section only
 */
export const useKPIData = (initialFilters = {}) => {
  const salesData = useSalesData(initialFilters);
  
  return {
    kpis: salesData.kpis,
    loading: salesData.loading,
    refreshing: salesData.refreshing,
    error: salesData.error,
    filters: salesData.filters,
    filterOptions: salesData.filterOptions,
    updateFilters: salesData.updateFilters,
    setDateRange: salesData.setDateRange,
    setCloser: salesData.setCloser,
    refresh: salesData.refresh,
    dataInfo: salesData.dataInfo
  };
};

/**
 * Hook for heatmap data (separate from main dashboard data)
 */
export const useHeatmapData = (tabType, dimension, initialFilters = {}) => {
  const [heatmapData, setHeatmapData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const defaultFilters = {
    aggregation_level: 'weekly',
    periods_back: 12,
    closer: 'all',
    ...initialFilters
  };
  
  const [filters, setFilters] = useState(defaultFilters);
  
  const fetchHeatmapData = useCallback(async () => {
    if (!tabType || !dimension) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        dimension,
        reference_date: filters.reference_date,
        aggregation_level: filters.aggregation_level,
        periods_back: filters.periods_back,
        closer: filters.closer
      };
      
      const data = await salesService.getHeatmapData(tabType, params);
      setHeatmapData(data);
    } catch (err) {
      console.error('Error fetching heatmap data:', err);
      setError('Failed to fetch heatmap data');
    } finally {
      setLoading(false);
    }
  }, [tabType, dimension, filters]);
  
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);
  
  useEffect(() => {
    fetchHeatmapData();
  }, [fetchHeatmapData]);
  
  return {
    data: heatmapData,
    loading,
    error,
    filters,
    updateFilters,
    refetch: fetchHeatmapData
  };
};

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Hook for managing filter state only (no data fetching)
 */
export const useFilterState = (initialFilters = {}) => {
  const [filters, setFilters] = useState({
    date_range: 'last_4_weeks',
    closer: 'all',
    offer_type: 'all',
    start_date: '',
    end_date: '',
    specific_date: '',
    date_filter_type: 'preset',
    ...initialFilters
  });

  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      date_range: 'last_4_weeks',
      closer: 'all', 
      offer_type: 'all',
      start_date: '',
      end_date: '',
      specific_date: '',
      date_filter_type: 'preset'
    });
  }, []);

  return {
    filters,
    updateFilters,
    resetFilters,
    setCloser: (closer) => updateFilters({ closer }),
    setOfferType: (offerType) => updateFilters({ offer_type: offerType }),
    setDateRange: (dateRange) => updateFilters({ 
      date_range: dateRange, 
      date_filter_type: 'preset',
      start_date: '',
      end_date: '',
      specific_date: ''
    })
  };
};

/**
 * Hook for sales data with real-time filtering (updates on every filter change)
 */
export const useRealTimeSalesData = (initialFilters = {}) => {
  const [bulkData, setBulkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const filterState = useFilterState(initialFilters);
  
  // Fetch data when date filters change
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const apiFilters = {};
        
        // Add date filters
        if (filterState.filters.start_date) apiFilters.start_date = filterState.filters.start_date;
        if (filterState.filters.end_date) apiFilters.end_date = filterState.filters.end_date;
        
        const bulk = await salesService.getBulkSalesData(apiFilters);
        setBulkData(bulk);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [filterState.filters.start_date, filterState.filters.end_date, filterState.filters.date_range]);
  
  // Calculate filtered data in real-time
  const filteredData = useMemo(() => {
    if (!bulkData?.raw_data) return null;
    
    const rawDataArray = bulkData.raw_data;
    return {
      kpis: salesService.calculateKPIs(rawDataArray, filterState.filters),
      teamData: salesService.calculateTeamPerformance(rawDataArray, filterState.filters.closer),
      callStatus: salesService.calculateCallStatus(rawDataArray, filterState.filters.closer),
      offers: salesService.calculateOffers(rawDataArray, filterState.filters.closer, filterState.filters.offer_type)
    };
  }, [bulkData, filterState.filters]);
  
  return {
    ...filteredData,
    ...filterState,
    bulkData,
    loading,
    error,
    refresh: () => {
      salesService.clearCache();
      // Will trigger refetch via useEffect
    }
  };
};